import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/shared/SearchableSelect';
import type { LookupExpenseType } from '@/lib/api/accounting';
import type { ExpenseEntry } from '@/lib/mock/expenses';

type Props = {
  open: boolean;
  entry: ExpenseEntry | null;
  expenseTypes: LookupExpenseType[];
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (expenseTypeId: string) => void;
};

const EditLedgerExpenseTypeModal: React.FC<Props> = ({
  open,
  entry,
  expenseTypes,
  saving = false,
  error = null,
  onClose,
  onSave,
}) => {
  const [expenseTypeId, setExpenseTypeId] = React.useState('');

  React.useEffect(() => {
    if (!open || !entry) return;
    const raw = entry.expenseTypeId || '';
    setExpenseTypeId(raw.startsWith('advance:') ? '' : raw);
  }, [open, entry]);

  if (!entry) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !saving) onClose();
      }}
    >
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>ویرایش نوع هزینه</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm text-slate-600">
            {entry.beneficiary || '—'}
            {entry.dateFa ? ` · ${entry.dateFa}` : ''}
            {entry.amount ? ` · ${entry.amount.toLocaleString('fa-IR')} ریال` : ''}
          </p>
          <div className="space-y-1.5">
            <Label>نوع هزینه</Label>
            <SearchableSelect
              value={expenseTypeId}
              onChange={setExpenseTypeId}
              options={[
                { value: '', label: 'بدون نوع هزینه' },
                ...expenseTypes.map((t) => ({ value: String(t.id), label: t.title })),
              ]}
              placeholder="انتخاب نوع هزینه…"
              emptyMessage="نوع هزینه‌ای یافت نشد"
            />
          </div>
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>
        <DialogFooter className="gap-2 sm:justify-start">
          <Button type="button" disabled={saving} onClick={() => onSave(expenseTypeId)}>
            {saving ? <Loader2 className="ml-2 h-4 w-4 animate-spin" /> : null}
            ذخیره
          </Button>
          <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
            انصراف
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditLedgerExpenseTypeModal;
